import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { LockCalculatorDefinition } from "../../../../../http/lock_calculator/types";
import { deriveInputSignatures } from "../../data/calculationAdapter";
import type { CalculationCoefficients, LockCalculatorFormValues } from "../../model/form";
import { blocksAutomaticGeneration, type NoticeCode } from "../../model/noticeCatalog";
import {
  buildFormNotices,
  buildManualGenerationNotices,
  buildSessionNotices,
  cascadeToNotice,
  dropAxisNoticesCoveredBy,
  fromBackendNotices,
  isCascadeActive,
  sortNotices,
  type CalculatorNotice,
  type CascadeRecord,
  type StalenessBaselines,
} from "../../model/notices";

export type ReportCascade = (record: CascadeRecord) => void;

/** The backend notice listing the required fields the generation lacks; it stays out of the list until the filling in starts. */
const MISSING_FIELDS_CODE: NoticeCode = "generationInputsMissing";

export type NoticesContextValue = {
  notices: CalculatorNotice[];
  reportCascade: ReportCascade;
  /** Notices that name this form path as one of their triggers, most severe first. */
  noticesForField: (path: string) => CalculatorNotice[];
  /** Opens the wizard step that owns the notice and focuses its first field. */
  goToNotice: (notice: CalculatorNotice) => void;
  /** Files an element ("go to" lands on it) under a form path or an `action:<mode>` key; null removes it. */
  registerTarget: (key: string, element: HTMLElement | null) => void;
  /** Set while a required field is missing for the automatic and semi-automatic generation: the buttons wait. */
  automaticBlock: CalculatorNotice | null;
};

const NoticesContext = createContext<NoticesContextValue>({
  notices: [],
  reportCascade: () => undefined,
  noticesForField: () => [],
  goToNotice: () => undefined,
  registerTarget: () => undefined,
  automaticBlock: null,
});

export const NoticesProvider = NoticesContext.Provider;
export const useNotices = () => useContext(NoticesContext);

/** A stable ref callback that files the element under `key` for "go to"; no key, nothing is filed. */
export const useNoticeTarget = (key: string | undefined) => {
  const { registerTarget } = useNotices();
  return useCallback((element: HTMLElement | null) => { if (key) registerTarget(key, element); }, [registerTarget, key]);
};

/** Cascade records that stay while their cause and effect still hold the values that produced them. */
const useCascadeRecords = (values: LockCalculatorFormValues | undefined, calculationId: string | undefined) => {
  const [records, setRecords] = useState<CascadeRecord[]>([]);
  // A record is only dropped once it has been seen active: right after it is
  // reported the watched values may not have caught up with the setValue yet.
  const armed = useRef(new Set<string>());

  const report = useCallback<ReportCascade>((record) => {
    armed.current.delete(record.id);
    setRecords((prev) => [...prev.filter((existing) => existing.id !== record.id), record]);
  }, []);

  useEffect(() => {
    armed.current.clear();
    setRecords([]);
  }, [calculationId]);

  useEffect(() => {
    if (!values) return;
    const keep = (record: CascadeRecord) => {
      if (isCascadeActive(record, values)) {
        armed.current.add(record.id);
        return true;
      }
      return !armed.current.has(record.id);
    };
    setRecords((prev) => (prev.every(keep) ? prev : prev.filter(keep)));
  }, [values, records]);

  const active = useMemo(
    () => (values ? records.filter((record) => isCascadeActive(record, values)) : []),
    [records, values],
  );
  return { active, report };
};

const hasAnyResistance = (calculation: LockCalculatorDefinition | null): boolean => {
  const result = calculation?.configuration?.result;
  return !!result && [result.fvD, result.faxD, result.flatD, result.fupD].some((value) => value != null);
};

/**
 * One registry of every notice: backend state, form-derived state, the cascades the rules applied and the
 * "modified after the last calculation" ones. `markProduced` is called when a calculation or a generation
 * returns, so later input changes show up as stale.
 */
export const useCalculatorNotices = ({
  values,
  initialValues,
  calculation,
  coefficients,
  calculationId,
}: {
  values: LockCalculatorFormValues | undefined;
  initialValues: LockCalculatorFormValues;
  calculation: LockCalculatorDefinition | null;
  coefficients: CalculationCoefficients;
  calculationId: string | undefined;
}) => {
  const { active: cascades, report: reportCascade } = useCascadeRecords(values, calculationId);

  const [baselines, setBaselines] = useState<StalenessBaselines>({
    result: null,
    drafts: null,
    draftsMode: "generateAll",
    lastGenerationEmpty: false,
    report: null,
  });
  // What the baselines read when a calculation opens or a generation returns: the latest values, without
  // making those effects depend on them.
  const latest = useRef({ initialValues, coefficients });
  useEffect(() => {
    latest.current = { initialValues, coefficients };
  });
  useEffect(() => {
    if (!calculationId) return;
    const signatures = deriveInputSignatures(latest.current.initialValues, latest.current.coefficients);
    setBaselines({
      result: signatures.full,
      drafts: signatures.definition,
      draftsMode: "generateAll",
      lastGenerationEmpty: false,
      report: null,
    });
  }, [calculationId]);

  const markProduced = useCallback(
    (
      produced: { result?: boolean; report?: boolean; drafts?: "generateAll" | "generateFromData"; generationEmpty?: boolean },
      form: LockCalculatorFormValues,
    ) => {
      const signatures = deriveInputSignatures(form, latest.current.coefficients);
      setBaselines((prev) => ({
        result: produced.result ? signatures.full : prev.result,
        report: produced.report ? signatures.report : prev.report,
        drafts: produced.drafts ? signatures.definition : prev.drafts,
        draftsMode: produced.drafts ?? prev.draftsMode,
        lastGenerationEmpty: produced.drafts ? !!produced.generationEmpty : prev.lastGenerationEmpty,
      }));
    },
    [],
  );

  const signatures = useMemo(() => (values?.environment ? deriveInputSignatures(values, coefficients) : null), [values, coefficients]);
  const hasResult = hasAnyResistance(calculation);
  const hasDrafts = (calculation?.draftConfigurationsTotal ?? 0) > 0;

  const { notices, automaticBlock } = useMemo(() => {
    const fromBackend = fromBackendNotices(calculation?.notices);
    const manualInputs = buildManualGenerationNotices(values, hasResult);
    // The automatic and semi-automatic generation cannot run when a required field is missing, or when
    // no candidate can be built at all (no compatible lock, fastener or lock stop): the buttons wait.
    // The backend always reports a missing field; a calculation nobody has started does not list it.
    const missingFields = fromBackend.find((notice) => notice.code === MISSING_FIELDS_CODE) ?? null;
    const noCandidate = fromBackend.find((notice) => notice !== missingFields && blocksAutomaticGeneration(notice.code)) ?? null;
    const automaticBlock = missingFields ?? noCandidate;
    const listed = dropAxisNoticesCoveredBy(
      fromBackend.filter((notice) => notice !== missingFields || notice.params["started"] !== false),
      manualInputs,
    );
    // A generation with a missing required field has its own notice; its "recalculate" one waits.
    const blocked = { manual: manualInputs.length > 0, automatic: automaticBlock !== null };
    return {
      automaticBlock,
      notices: sortNotices([
        ...listed,
        ...buildFormNotices(values, coefficients),
        ...manualInputs,
        ...cascades.map(cascadeToNotice),
        ...buildSessionNotices(signatures, baselines, hasResult, hasDrafts, blocked),
      ]),
    };
  }, [calculation?.notices, values, coefficients, cascades, signatures, baselines, hasResult, hasDrafts]);

  return { notices, automaticBlock, reportCascade, markProduced };
};
